import React, { useState, useRef } from 'react';
import InfoTracking from '@app/components/common/InfoTracking';
import { Tooltip, Button, Col, Switch, DatePicker, Drawer, Form, Input, Row, Select, InputNumber, Space, message, Modal } from 'antd';
import { CloseOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {insertDetail, getDetail, updateDetail} from '../../../../../apis/PeriodosApi';
import dayjs from 'dayjs';
import './styles.scss';

const App = ({title, periodId, period, id, open, finish}) => {
    const [ form ] = Form.useForm()
    const [ID, setID] = useState(id);
    const [showModal, setShowModal] = useState(false);
    const [initialValues, setInitialValues] = useState(null);

    // Función helper para convertir fechas de manera segura
    const convertToDayjs = (dateValue) => {
        if (!dateValue) return null;
        
        // Si ya es un objeto dayjs válido, retornarlo
        if (dayjs.isDayjs(dateValue) && dateValue.isValid()) {
            return dateValue;
        }
        
        // Si es una cadena, convertirla a dayjs
        if (typeof dateValue === 'string') {
            const dayjsDate = dayjs(dateValue, 'YYYY-MM-DD');
            return dayjsDate.isValid() ? dayjsDate : null;
        }
        
        // Si es un objeto Date, convertirlo a dayjs
        if (dateValue instanceof Date) {
            const dayjsDate = dayjs(dateValue);
            return dayjsDate.isValid() ? dayjsDate : null;
        }
        
        return null;
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if(!values) return;
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            return;
        }

        const fields = form.getFieldsValue()
        
        // Convertir fechas dayjs a formato string para enviar al API
        const processedFields = { ...fields };
        
        if (processedFields.fecha_inicial && dayjs.isDayjs(processedFields.fecha_inicial)) {
            processedFields.fecha_inicial = processedFields.fecha_inicial.format('YYYY-MM-DD');
        }
        
        if (processedFields.fecha_final && dayjs.isDayjs(processedFields.fecha_final)) {
            processedFields.fecha_final = processedFields.fecha_final.format('YYYY-MM-DD');
        }
        
        if (processedFields.fecha_pago && dayjs.isDayjs(processedFields.fecha_pago)) {
            processedFields.fecha_pago = processedFields.fecha_pago.format('YYYY-MM-DD');
        }
        
        if (processedFields.fecha_timbrado && dayjs.isDayjs(processedFields.fecha_timbrado)) {
            processedFields.fecha_timbrado = processedFields.fecha_timbrado.format('YYYY-MM-DD');
        }

        var defaultValue = {
            // "id": 0,
            // "anio": 0,
            // "descripcion": "",
            // "periodo_especial": false,
            // "fecha_inicial": '',
            // "fecha_final":  '',
            // "dias_periodo":  '',
            // "horas_periodo": 0,
            // "mes": 0,
            // "num_per_del_mes": 0,
            // "total_per_del_mes": 0,
            // "fin_de_mes": false,
            // "no_descontar_saldos": false,
            // "solo_excepciones": false,
            // "incluir_bajas": false,
            // "anio_contable": 0,
            // "mes_contable": 0,
            // "tipo_nomina": "O",
            // "clave_sat": ''
        }
        
        if(ID > 0){
            const payload = {
                id: ID,
                ...defaultValue,
                ...processedFields
            }
            const response = await updateDetail(payload);
            if(response.ok){
                message.success('Se guardo correctamente el periodo!');
                setShowModal(false);
                finish(true);
            }
        }else{
            const payload = {
                periodo_id: periodId,
                ...processedFields
            }
            const response = await insertDetail(payload);
            if(response.ok){
                message.success('Se guardo correctamente el periodo!');
                setShowModal(false);
                finish(true);
            }
        }
    }

    const onClose = ()=>{
        setShowModal(false);
        finish(false);
    }

    React.useEffect(() => {
        setShowModal(open);
    }, [open]);

    React.useEffect(() => {
        setID(id);
    }, [id]);

    const effectRan = useRef(false);
    React.useEffect(() => {
      if (!effectRan.current) {
        (async ()=>{
            if(ID > 0){
                await refreshData();
            }else{
                setInitialValues({
                    clave_sat: period?.clave_sat || '',
                    periodo_especial: false,
                    no_descontar_saldos: false,
                    solo_excepciones: false,
                    incluir_bajas: false,
                    fin_de_mes: false,
                    tipo_nomina: "O"
                });
            }
        })()
      }
      return () => effectRan.current = true;
    }, [ID]);

    const setValueInitial = async (property, value)=> {
        const fields = form.getFieldsValue()
        fields[property] = value;
        form.setFieldsValue(fields);
    }

    const validateFinMes = async ()=> {
        const fields = form.getFieldsValue()
        fields.fin_de_mes = fields.num_per_del_mes == fields.total_per_del_mes;
        form.setFieldsValue(fields);
    }

    const refreshData = async ()=> {
        if(ID > 0){
            const response = await getDetail(ID);
            if(response.ok){
                let detail = { ...response.payload };
                
                // Convertir fechas de manera segura
                if(detail.fecha_inicial){
                    detail.fecha_inicial = convertToDayjs(detail.fecha_inicial);
                }

                if(detail.fecha_final){
                    detail.fecha_final = convertToDayjs(detail.fecha_final);
                }
                
                if(detail.fecha_pago){
                    detail.fecha_pago = convertToDayjs(detail.fecha_pago);
                }
                
                if(detail.fecha_timbrado){
                    detail.fecha_timbrado = convertToDayjs(detail.fecha_timbrado);
                }
                
                setInitialValues(detail);
            }
        }else{
            setInitialValues({
                clave_sat: period?.clave_sat || '',
                periodo_especial: false,
                no_descontar_saldos: false,
                solo_excepciones: false,
                incluir_bajas: false,
                fin_de_mes: false,
                tipo_nomina: "O"
            });
        }
    }

  return (
    <>
      <Drawer
        title={title}
        width={720}
        placement={"right"}
        onClose={onClose}
        open={open}
        closable={true}
        height={'80%'}
        onFinish={handleOk}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
        extra={[
            <Space key="drawer-actions">
                {ID && <InfoTracking entity={'ConfiguracionPeriodoDetalle'} entityId={ID}/>}
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleOk} type="primary">
                Guardar
                </Button>
            </Space>
        ]}
      >
        {
            initialValues && 
            <Form layout="vertical" form={form} requiredMark onFinish={handleOk} initialValues={initialValues}>
                <Row gutter={16}>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="anio"
                            label="Año"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega un año',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega un año" onChange={(value)=>{setValueInitial("anio_contable", value)}} style={{width: '80%',}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="num_periodo"
                            label="Número de periodo"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega un número de periodo',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega un número de periodo"  style={{width: '80%',}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="descripcion"
                            label="Descripción"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega una descripción',
                            },
                            ]}
                        >
                            <Input placeholder="Por favor agrega una descripción" />
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="periodo_especial"
                            label="Periodo Especial"
                        >
                            <Switch 
                                    checkedChildren={<CheckOutlined />}
                                    unCheckedChildren={<CloseOutlined />}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="fecha_inicial"
                            label="Fecha Inicial"
                            rules={[
                                {
                                    required: true,
                                    message: 'Por favor agrega una fecha',
                                },
                                ]}
                        >
                            <DatePicker placeholder="DD/MM/YYYY"  style={{width: '100%',}} format={'DD/MM/YYYY'}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="fecha_final"
                            label="Fecha Final"
                            rules={[
                                {
                                    required: true,
                                    message: 'Por favor agrega una fecha',
                                },
                                ]}
                        >
                            <DatePicker placeholder="DD/MM/YYYY" style={{width: '100%',}} format={'DD/MM/YYYY'} />
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="fecha_pago"
                            label="Fecha Pago"
                            rules={[
                                {
                                    required: true,
                                    message: 'Por favor agrega una fecha',
                                },
                                ]}
                        >
                            <DatePicker placeholder="DD/MM/YYYY"  style={{width: '100%',}} format={'DD/MM/YYYY'}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="fecha_timbrado"
                            label="Fecha Timbrado"
                            rules={[
                                {
                                    required: true,
                                    message: 'Por favor agrega una fecha',
                                },
                                ]}
                        >
                            <DatePicker placeholder="DD/MM/YYYY" style={{width: '100%',}} format={'DD/MM/YYYY'} />
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="dias_periodo"
                            label="Días Periodo"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega los días del periodo',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega los días del periodo" style={{width: '80%',}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="horas_periodo"
                            label="Horas Periodo"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega las horas del periodo',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega las horas del periodo" style={{width: '80%',}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="mes"
                            label="Mes"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega el mes',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega el mes" style={{width: '80%',}} onChange={(value)=>{setValueInitial("mes_contable", value)}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="num_per_del_mes"
                            label="Num Per del Mes"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega el num per del mes',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega el num per del mes" style={{width: '80%',}}  onChange={(value)=>{validateFinMes()}} />
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="total_per_del_mes"
                            label={
                                <div>
                                  Total Per del Mes {" "}
                                  <Tooltip title="Nombre variable periodo_total_per_del_mes">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </div>
                            }
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega el total per del mes',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega el total per del mes" style={{width: '80%',}} onChange={(value)=>{validateFinMes()}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="fin_de_mes"
                            label="Fin de Mes"
                        >
                            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="no_descontar_saldos"
                            label={
                                <div>
                                  No descontar saldos {" "}
                                  <Tooltip title="Nombre variable periodo_no_descontar_saldos">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </div>
                            }>
                            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="solo_excepciones"
                            label={
                                <div>
                                  Solo Excepciones {" "}
                                  <Tooltip title="Nombre variable periodo_solo_excepciones">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </div>
                            }>
                            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="incluir_bajas"
                            label={
                                <div>
                                  Incluir Bajas {" "}
                                  <Tooltip title="Nombre variable periodo_incluir_bajas">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </div>
                            }>
                            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="anio_contable"
                            label="Año contable"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega el año contable',
                            },
                            ]}>
                            <Input placeholder="Por favor agrega el año contable" style={{width: '80%',}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="mes_contable"
                            label="Mes contable"
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega el mes contable',
                            },
                            ]}>
                            <InputNumber placeholder="Por favor agrega el mes contable" style={{width: '80%',}}/>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="tipo_nomina"
                            label={
                                <div>
                                  Tipo de nomina {" "}
                                  <Tooltip title="Nombre variable periodo_tipo_nomina">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </div>
                            }
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega el tipo de nomina',
                            },
                            ]}>
                            <Select placeholder="Por favor agrega el tipo de nomina">
                                <Select.Option value="O">O</Select.Option>
                                <Select.Option value="E">E</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col md={{span: 12}} sm={{span: 12}} xs={{span: 24}}>
                        <Form.Item
                            name="clave_sat"
                            label={
                                <div>
                                  Clave SAT del Periodo {" "}
                                  <Tooltip title="Nombre variable periodo_clave_sat">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </div>
                            }
                            rules={[
                            {
                                required: true,
                                message: 'Por favor agrega la clave SAT del periodo',
                            },
                            ]}
                        >
                            <Input placeholder="Por favor agrega la clave SAT del periodo" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        }
      </Drawer>
    </>
  );
};

export default App;